import { useEffect, useMemo, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { AlertOctagon, MoreHorizontal } from 'lucide-react';
import type { WorkAction, WorkItemListItem, WorkPhase } from '@vwt/contracts';
import { Avatar, Button, cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, toast } from '@vwt/ui';
import { api, newIdempotencyKey, queryClient } from '../../lib/api.js';
import { errorMessage } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { DueLabel, KeyLabel, PriorityLabel, ProgressPill } from '../../components/badges.js';
import { TransitionDialog } from './work-detail.js';
import { groupItems, type LaneField } from './grouping.js';

const COLUMNS: WorkPhase[] = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'INTEGRATED', 'DONE'];

/** Maps a column move to the guarded transition the server will evaluate. */
function actionFor(from: WorkPhase, to: WorkPhase, policy: string): WorkAction | null {
  if (from === to) return null;
  if (from === 'TODO' && to === 'IN_PROGRESS') return 'start';
  if (from === 'IN_PROGRESS' && to === 'IN_REVIEW') return 'request_review';
  if (from === 'IN_REVIEW' && to === 'IN_PROGRESS') return 'request_changes';
  if ((from === 'DONE' || from === 'CANCELED') && to === 'IN_PROGRESS') return 'reopen';
  if (to === 'DONE' && policy === 'DELIVERABLE') return 'complete_deliverable';
  if (to === 'DONE' && policy === 'REVIEWED_DELIVERABLE') return 'approve_deliverable';
  return null;
}

/**
 * Board over the fixed work phases. CANCELED is hidden by default. Drag-and-drop and the card menu
 * (keyboard alternative) both request server-guarded transitions; nothing fakes production completion.
 */
export function WorkBoard({ items, onOpen, lanes = null }: { items: WorkItemListItem[]; onOpen: (id: string) => void; lanes?: LaneField | null }) {
  const { t } = useTranslation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));
  const [dialog, setDialog] = useState<{ item: WorkItemListItem; action: WorkAction } | null>(null);
  const rows = useMemo(
    () => (lanes ? groupItems(items, lanes).map((g) => ({ key: g.key || '__none', label: 'i18n' in g.label ? t(g.label.i18n) : g.label.text, items: g.items })) : [{ key: '__all', label: '', items }]),
    [items, lanes, t],
  );

  const move = async (item: WorkItemListItem, to: WorkPhase) => {
    const action = actionFor(item.phase, to, item.completionPolicy);
    if (!action) {
      toast.error(t('work.board.dropRejected', { reason: item.completionPolicy === 'PRODUCTION' && (to === 'DONE' || to === 'INTEGRATED') ? t('work.board.productionHint') : `${t(`status.phase.${item.phase}`)} → ${t(`status.phase.${to}`)}` }));
      return;
    }
    if (['request_changes', 'reopen', 'complete_deliverable', 'cancel'].includes(action) || (action === 'request_review' && item.completionPolicy === 'REVIEWED_DELIVERABLE')) {
      setDialog({ item, action });
      return;
    }
    try {
      await api.call('transitionWorkItem', { params: { id: item.id }, idempotencyKey: newIdempotencyKey(), body: { expectedVersion: item.version, action } });
      await queryClient.invalidateQueries({ queryKey: ['listWorkItems'] });
    } catch (error) {
      toast.error(t('work.board.dropRejected', { reason: errorMessage(error) }));
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    // Droppable ids are "<lane>|<phase>": moving across rows changes only the phase.
    const to = event.over ? (String(event.over.id).split('|').pop() as WorkPhase) : undefined;
    const item = items.find((i) => i.id === event.active.id);
    if (to && item) void move(item, to);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className={cn('flex flex-col gap-4 p-4', !lanes && 'h-full')}>
        {rows.map((lane) => (
          <section key={lane.key} aria-label={lane.label || undefined}>
            {lanes ? (
              <h2 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-fg-muted">
                {lane.label} <span className="font-normal text-fg-subtle">{lane.items.length}</span>
              </h2>
            ) : null}
            <div className={cn('flex gap-3 overflow-x-auto', lanes ? 'min-h-40' : 'h-full min-h-[400px]')}>
              {COLUMNS.map((phase) => {
                const cards = lane.items.filter((i) => i.phase === phase);
                return (
                  <Column key={phase} id={`${lane.key}|${phase}`} phase={phase} count={cards.length}>
                    {cards.map((item) => (
                      <Card key={item.id} item={item} onOpen={onOpen} onMove={(to) => void move(item, to)} />
                    ))}
                  </Column>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      {dialog ? <DialogLoader id={dialog.item.id} action={dialog.action} onClose={() => setDialog(null)} /> : null}
    </DndContext>
  );
}

function DialogLoader({ id, action, onClose }: { id: string; action: WorkAction; onClose: () => void }) {
  const [item, setItem] = useState<Awaited<ReturnType<typeof api.call<'getWorkItem'>>> | null>(null);
  useEffect(() => {
    let active = true;
    void api.call('getWorkItem', { params: { id } }).then((result) => active && setItem(result));
    return () => {
      active = false;
    };
  }, [id]);
  return item ? <TransitionDialog item={item.data} action={action} onClose={onClose} /> : null;
}

function Column({ id, phase, count, children }: { id: string; phase: WorkPhase; count: number; children: React.ReactNode }) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <section ref={setNodeRef} aria-label={t(`status.phase.${phase}`)} className={cn('flex min-w-64 flex-1 flex-col rounded-[var(--radius-lg)] border border-border bg-surface-2', isOver && 'border-accent bg-accent-soft/30')}>
      <header className="flex items-center justify-between px-3 py-2">
        <h2 className="text-xs font-semibold tracking-wide text-fg-muted uppercase">{t(`status.phase.${phase}`)}</h2>
        <span className="text-xs text-fg-subtle">{count}</span>
      </header>
      <div className="flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2">{children}</div>
    </section>
  );
}

function Card({ item, onOpen, onMove }: { item: WorkItemListItem; onOpen: (id: string) => void; onMove: (to: WorkPhase) => void }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <article
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => onOpen(item.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(item.id);
        listeners?.onKeyDown?.(e);
      }}
      className={cn('cursor-grab rounded-[var(--radius)] border border-border bg-surface p-2.5 text-sm shadow-xs hover:border-border-strong', isDragging && 'z-50 opacity-80 shadow-lg')}
    >
      <div className="mb-1 flex items-center gap-2">
        <KeyLabel value={item.key} />
        {item.blocked ? <AlertOctagon className="size-3.5 text-danger" aria-label={t('work.detail.blockedNote')} /> : null}
        <div className="ml-auto" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="iconSm" variant="ghost" aria-label={t('work.board.moveTo')}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t('work.board.moveTo')}</DropdownMenuLabel>
              {COLUMNS.filter((p) => p !== item.phase).map((p) => (
                <DropdownMenuItem key={p} onSelect={() => onMove(p)}>
                  {t(`status.phase.${p}`)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <p className="line-clamp-3 font-medium text-fg">{item.title}</p>
      {item.childProgress ? <ProgressPill progress={item.childProgress} className="mt-1.5" /> : null}
      <div className="mt-2 flex items-center gap-2">
        <PriorityLabel priority={item.priority} compact />
        <DueLabel date={item.dueDate} phase={item.phase} compact />
        <span className="truncate text-xs text-fg-subtle">{item.project?.key ?? item.team?.name ?? ''}</span>
        {item.assignee ? <Avatar name={item.assignee.displayName} size="sm" className="ml-auto" /> : null}
      </div>
    </article>
  );
}
